import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackShipment } from '../services/api';
import './Tracking.css';

const STATUS_ICONS = {
  pre_transit: '📋',
  in_transit: '🚚',
  out_for_delivery: '🏃',
  delivered: '✅',
  return_to_sender: '↩️',
  failure: '❌',
  unknown: '❓',
};

export default function Tracking() {
  const [searchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState(searchParams.get('tracking') || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (searchParams.get('tracking')) {
      handleTrack(searchParams.get('tracking'));
    }
  }, []);

  const handleTrack = async (num = trackingNumber) => {
    if (!num.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await trackShipment(num.trim());
      setResult(data);
    } catch {
      setError('Tracking number not found. Please double-check and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem', maxWidth: '700px' }}>
      <div className="page-header">
        <h1>Track Your Order</h1>
        <p>Enter your tracking number or order number to see live updates.</p>
      </div>

      <div className="track-form card">
        <div className="track-input-row">
          <input
            className="form-control"
            placeholder="Enter tracking number..."
            value={trackingNumber}
            onChange={e => setTrackingNumber(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleTrack()}
          />
          <button className="btn btn-primary" onClick={() => handleTrack()} disabled={loading}>
            {loading ? '...' : 'Track'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {result && (
        <div className="tracking-result">
          {/* Header */}
          <div className="card tracking-header">
            <div className="tracking-status-icon">
              {STATUS_ICONS[result.status] || '📦'}
            </div>
            <div>
              <p className="tracking-number-label">{result.trackingNumber}</p>
              <h2 className="tracking-status-text">{result.status?.replace(/_/g, ' ').toUpperCase()}</h2>
              {result.carrier && <p className="tracking-carrier">via {result.carrier}</p>}
              {result.estimatedDelivery && (
                <p className="tracking-eta">
                  Est. Delivery: <strong>{new Date(result.estimatedDelivery).toLocaleDateString()}</strong>
                </p>
              )}
            </div>
          </div>

          {/* Events Timeline */}
          {result.events?.length > 0 && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>Tracking History</h3>
              <div className="timeline">
                {result.events.map((event, i) => (
                  <div key={i} className={`timeline-event ${i === 0 ? 'latest' : ''}`}>
                    <div className="timeline-dot" />
                    <div className="timeline-content">
                      <p className="timeline-message">{event.message}</p>
                      {event.location && <p className="timeline-location">📍 {event.location}</p>}
                      {event.datetime && (
                        <p className="timeline-time">
                          {new Date(event.datetime).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.order && (
            <div className="alert alert-info" style={{ marginTop: '1rem' }}>
              Order #{result.order.orderNumber} — Status: {result.order.status}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
